/**
 * Zustand store for managing the character catalog.
 * Wraps characterDatabase.ts and provides in-memory state management.
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { CharacterDraft } from './types/characterDraft';
import type { RuntimeState } from '../utils/characterExport';
import {
  initDatabase,
  getAllCharacters,
  saveCharacter,
  getCharacter,
  deleteCharacter as dbDeleteCharacter,
  duplicateCharacter as dbDuplicateCharacter,
  renameCharacter as dbRenameCharacter,
  clearAllCharacters,
  type StoredCharacter,
} from '../utils/characterDatabase';

// ============================================================================
// Types
// ============================================================================

/**
 * Character catalog store state and actions.
 */
export interface CharacterCatalogState {
  // State
  /** All characters in the catalog */
  characters: StoredCharacter[];
  /** Whether the catalog is currently loading */
  isLoading: boolean;
  /** Error message from last operation, or null */
  error: string | null;
  /** Whether the catalog has been initialized from IndexedDB */
  isInitialized: boolean;

  // Initialization Actions
  /** Load all characters from IndexedDB into memory */
  initializeCatalog: () => Promise<void>;
  /** Re-sync characters from IndexedDB */
  refreshCatalog: () => Promise<void>;

  // CRUD Actions
  /** Save a character to the catalog (create or update) */
  saveCharacterToCatalog: (
    draft: CharacterDraft,
    runtimeState?: RuntimeState
  ) => Promise<StoredCharacter | null>;
  /** Load a specific character by ID */
  loadCharacter: (id: string) => Promise<StoredCharacter | null>;
  /** Delete a character from the catalog */
  deleteCharacterFromCatalog: (id: string) => Promise<boolean>;
  /** Duplicate a character with a new ID */
  duplicateCharacterInCatalog: (id: string) => Promise<StoredCharacter | null>;
  /** Rename a character */
  renameCharacterInCatalog: (
    id: string,
    newName: string
  ) => Promise<StoredCharacter | null>;

  // Query Actions (synchronous - operates on in-memory state)
  /** Get characters filtered by ruleset ID */
  getCharactersByRuleset: (rulesetId: string) => StoredCharacter[];

  // Utility Actions
  /** Clear the error state */
  clearError: () => void;
  /** Clear all characters (for testing/reset) */
  clearCatalog: () => Promise<void>;
}

// ============================================================================
// Store Implementation
// ============================================================================

/**
 * Zustand store for managing the character catalog.
 *
 * Usage:
 * ```tsx
 * const { characters, initializeCatalog, saveCharacterToCatalog } = useCharacterCatalogStore();
 *
 * useEffect(() => {
 *   initializeCatalog();
 * }, [initializeCatalog]);
 * ```
 */
export const useCharacterCatalogStore = create<CharacterCatalogState>()(
  immer((set, get) => ({
    // ========================================================================
    // State
    // ========================================================================
    characters: [],
    isLoading: false,
    error: null,
    isInitialized: false,

    // ========================================================================
    // Initialization Actions
    // ========================================================================

    initializeCatalog: async () => {
      // Prevent re-initialization
      if (get().isInitialized) {
        return;
      }

      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      // Initialize the database
      const initResult = await initDatabase();
      if (!initResult.success) {
        set((state) => {
          state.isLoading = false;
          state.error = initResult.errors[0] || 'Failed to initialize database';
        });
        return;
      }

      // Load all characters
      const result = await getAllCharacters();

      set((state) => {
        state.isLoading = false;
        state.isInitialized = true;
        if (result.success) {
          state.characters = result.data || [];
        } else {
          state.error = result.errors[0] || 'Failed to load characters';
        }
      });
    },

    refreshCatalog: async () => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      const result = await getAllCharacters();

      set((state) => {
        state.isLoading = false;
        if (result.success) {
          state.characters = result.data || [];
        } else {
          state.error = result.errors[0] || 'Failed to refresh characters';
        }
      });
    },

    // ========================================================================
    // CRUD Actions
    // ========================================================================

    saveCharacterToCatalog: async (draft, runtimeState) => {
      set((state) => {
        state.error = null;
      });

      const result = await saveCharacter(draft, runtimeState);

      if (!result.success || !result.data) {
        set((state) => {
          state.error = result.errors[0] || 'Failed to save character';
        });
        return null;
      }

      const savedCharacter = result.data;

      // Update in-memory state
      set((state) => {
        const existingIndex = state.characters.findIndex(
          (c) => c.id === savedCharacter.id
        );
        if (existingIndex >= 0) {
          // Update existing
          state.characters[existingIndex] = savedCharacter;
        } else {
          // Add new (at beginning for "newest first" order)
          state.characters.unshift(savedCharacter);
        }
      });

      return savedCharacter;
    },

    loadCharacter: async (id) => {
      set((state) => {
        state.error = null;
      });

      const result = await getCharacter(id);

      if (!result.success || !result.data) {
        set((state) => {
          state.error = result.errors[0] || 'Failed to load character';
        });
        return null;
      }

      return result.data;
    },

    deleteCharacterFromCatalog: async (id) => {
      set((state) => {
        state.error = null;
      });

      const result = await dbDeleteCharacter(id);

      if (!result.success) {
        set((state) => {
          state.error = result.errors[0] || 'Failed to delete character';
        });
        return false;
      }

      // Remove from in-memory state
      set((state) => {
        state.characters = state.characters.filter((c) => c.id !== id);
      });

      return true;
    },

    duplicateCharacterInCatalog: async (id) => {
      set((state) => {
        state.error = null;
      });

      const result = await dbDuplicateCharacter(id);

      if (!result.success || !result.data) {
        set((state) => {
          state.error = result.errors[0] || 'Failed to duplicate character';
        });
        return null;
      }

      const duplicatedCharacter = result.data;

      // Add to in-memory state (at beginning for "newest first" order)
      set((state) => {
        state.characters.unshift(duplicatedCharacter);
      });

      return duplicatedCharacter;
    },

    renameCharacterInCatalog: async (id, newName) => {
      set((state) => {
        state.error = null;
      });

      const result = await dbRenameCharacter(id, newName);

      if (!result.success || !result.data) {
        set((state) => {
          state.error = result.errors[0] || 'Failed to rename character';
        });
        return null;
      }

      const renamedCharacter = result.data;

      // Update in-memory state
      set((state) => {
        const index = state.characters.findIndex((c) => c.id === id);
        if (index >= 0) {
          state.characters[index] = renamedCharacter;
        }
      });

      return renamedCharacter;
    },

    // ========================================================================
    // Query Actions
    // ========================================================================

    getCharactersByRuleset: (rulesetId) => {
      return get().characters.filter((c) => c.rulesetId === rulesetId);
    },

    // ========================================================================
    // Utility Actions
    // ========================================================================

    clearError: () => {
      set((state) => {
        state.error = null;
      });
    },

    clearCatalog: async () => {
      set((state) => {
        state.error = null;
      });

      const result = await clearAllCharacters();

      if (!result.success) {
        set((state) => {
          state.error = result.errors[0] || 'Failed to clear catalog';
        });
        return;
      }

      set((state) => {
        state.characters = [];
      });
    },
  }))
);

// ============================================================================
// Exports
// ============================================================================

export type { StoredCharacter };
